import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../../services/api.js';
import Logo from '../../assets/Mask_group.png';

const MIN_PASSWORD_LENGTH = 12;

function getPasswordStrength(password) {
  if (!password) return null;

  let score = 0;

  if (password.length >= MIN_PASSWORD_LENGTH) score += 1;
  if (password.length >= 16) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  if (score <= 2) return { label: 'Weak', color: 'bg-red-500', textColor: 'text-red-400', width: 'w-1/3' };
  if (score <= 3) return { label: 'Medium', color: 'bg-yellow-500', textColor: 'text-yellow-400', width: 'w-2/3' };
  return { label: 'Strong', color: 'bg-green-500', textColor: 'text-green-400', width: 'w-full' };
}

export default function SetPassword() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const access_token = state?.access_token;
  const refresh_token = state?.refresh_token;

  const [pass, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const strength = getPasswordStrength(pass);

  const handleChange = (e) => {
    setPassword(e.target.value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (pass.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setLoading(true);

    try {
      const response = await API.post('/set-password/', {
        access_token,
        refresh_token,
        password: pass,
      }, { skipAuth: true });

      if (response.data.status === 'success') {
        setSuccess('Password updated successfully! Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError('Could not set your password. Please request a new reset link and try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again or request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0E] flex items-center justify-center px-4 py-12">
      {/* Subtle grid overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />

      <div className="relative w-full max-w-md">
        {/* Card */}
        <div className="bg-[#101012] border border-[#232325] rounded-2xl p-8 shadow-2xl">

          {/* Logo + Branding */}
          <div className="flex flex-col items-center mb-8">
            <img
              src={Logo}
              alt="LLMinate logo"
              className="h-16 w-16 object-contain mb-3"
            />
            <h1 className="text-2xl font-bold tracking-widest text-white uppercase">
              LLMinate
            </h1>
            <p className="text-gray-400 text-sm mt-1">AI Red-Teaming Platform</p>
          </div>

          {/* Heading */}
          <h2 className="text-xl font-semibold text-white mb-1">Set your password</h2>
          <p className="text-gray-400 text-sm mb-6">
            Create a strong password to secure your account.
          </p>

          {/* Error Banner */}
          {error && (
            <div
              role="alert"
              className="mb-5 flex items-start gap-3 bg-red-500/10 border border-red-500/40 rounded-lg px-4 py-3"
            >
              <svg className="w-4 h-4 text-red-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Success Banner */}
          {success && (
            <div
              role="status"
              className="mb-5 flex items-start gap-3 bg-green-500/10 border border-green-500/40 rounded-lg px-4 py-3"
            >
              <svg className="w-4 h-4 text-green-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-green-400 text-sm">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* Password input */}
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-300 mb-1.5">
                New password
              </label>
              <input
                id="new-password"
                type="password"
                name="password"
                autoComplete="new-password"
                placeholder="••••••••••••"
                value={pass}
                onChange={handleChange}
                className={`w-full bg-[#1a1a1c] text-white placeholder-gray-600 border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-colors ${
                  error ? 'border-red-500/60' : 'border-[#232325] focus:border-blue-500'
                }`}
              />

              {/* Helper text — always visible */}
              <p className="mt-2 text-xs text-gray-500">
                Minimum {MIN_PASSWORD_LENGTH} characters. Use uppercase, numbers, and symbols for a stronger password.
              </p>

              {/* Strength meter — shown once user starts typing */}
              {pass.length > 0 && strength && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-gray-500">Password strength</span>
                    <span className={`text-xs font-medium ${strength.textColor}`}>
                      {strength.label}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-[#232325] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${strength.color} ${strength.width}`}
                    />
                  </div>

                  {/* Requirement checklist */}
                  <ul className="mt-3 space-y-1" aria-label="Password requirements">
                    <PasswordRequirement met={pass.length >= MIN_PASSWORD_LENGTH} label={`At least ${MIN_PASSWORD_LENGTH} characters`} />
                    <PasswordRequirement met={/[A-Z]/.test(pass)} label="One uppercase letter" />
                    <PasswordRequirement met={/[0-9]/.test(pass)} label="One number" />
                    <PasswordRequirement met={/[^A-Za-z0-9]/.test(pass)} label="One special character" />
                  </ul>
                </div>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors text-sm"
            >
              {loading && (
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {loading ? "Saving password..." : "Set password"}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-6 pt-6 border-t border-[#232325] text-center">
            <p className="text-gray-500 text-xs">
              LLMinate &mdash; Securing AI systems through adversarial testing
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PasswordRequirement({ met, label }) {
  return (
    <li className="flex items-center gap-2">
      {met ? (
        <svg className="w-3.5 h-3.5 text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5 text-gray-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="12" cy="12" r="9" />
        </svg>
      )}
      <span className={`text-xs ${met ? 'text-green-400' : 'text-gray-500'}`}>{label}</span>
    </li>
  );
}
